<template>
  <div class="format-asset-management">
    <div class="d-flex align-center mb-2">
      <v-btn
        icon
        small
        class="mr-1"
        :title="collapsed ? $t('expand') : $t('collapse')"
        @click="collapsed = !collapsed"
      >
        <v-icon>{{ collapsed ? 'mdi-menu-right' : 'mdi-menu-down' }}</v-icon>
      </v-btn>
      <span class="text-caption font-weight-medium text-uppercase">
        {{ $t('markup.assets') }}
      </span>
      <span class="text-caption grey--text ml-2">
        ({{ assets.length }})
      </span>
      <v-btn
        v-if="formatInstance?.job_deliver?.folder_node_id"
        text
        x-small
        class="ml-1"
        color="info"
        :title="$t('open_contents_folder')"
        @click="openContentsFolder"
      >
        <span class="text-decoration-underline">{{ $t('open_contents_folder') }}</span>
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn
        v-if="selectedAssetNodeIds.length"
        small
        color="primary"
        @click="downloadSelectedAssets"
      >
        <v-icon left small>mdi-download-multiple</v-icon>
        {{ $t('download') }} ({{ selectedAssetNodeIds.length }})
      </v-btn>
    </div>

    <template v-if="!collapsed">
      <!-- Loading Indicator -->
      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-3"></v-progress-linear>

      <div class="assets-container" v-else>
      <div class="assets-grid">
        <!-- Existing Asset Cards -->
        <div
          v-for="asset in assets"
          :key="asset.node_id"
          class="asset-card"
        >
          <v-card outlined class="asset-card-inner">
            <!-- Thumbnail -->
            <div class="asset-thumbnail" style="cursor: pointer;" @click="approveAsset(asset)">
              <v-checkbox
                :input-value="isAssetSelected(asset)"
                class="asset-selection-checkbox ma-0 pa-0"
                hide-details
                @click.stop
                @change="toggleAssetSelection(asset, $event)"
              ></v-checkbox>
              <v-img
                v-if="asset.thumbnail"
                :src="asset.thumbnail"
                class="grey lighten-2 fill-height"
                cover
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="grey lighten-1"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
              <div v-else class="asset-thumbnail-placeholder">
                <v-icon large color="grey lighten-1">mdi-file-outline</v-icon>
              </div>

              <!-- Status Badge -->
              <div class="asset-status-badge">
                <v-chip
                  v-if="asset.status === 'rejected'"
                  small
                  color="error"
                  label
                  class="px-1"
                >
                  <v-icon small>mdi-close-thick</v-icon>
                </v-chip>
                <v-chip
                  v-else-if="asset.status === 'approved'"
                  small
                  color="success"
                  label
                  class="px-1"
                >
                  <v-icon small>mdi-check-bold</v-icon>
                </v-chip>
                <v-chip
                  v-else-if="asset.status === 'approved_finalize'"
                  small
                  color="warning"
                  label
                  class="px-1"
                >
                  <v-icon small>mdi-check-bold</v-icon>
                </v-chip>
                <v-chip
                  v-else-if="asset.status === 'pending'"
                  small
                  label
                  class="px-1"
                >
                  <v-icon small>mdi-clock-outline</v-icon>
                </v-chip>
              </div>

              <!-- Latest Version Badge -->
              <v-chip
                v-if="asset.latestNode && asset.latestNode.version"
                small
                label
                color="primary"
                class="asset-latest-version-badge white--text px-2"
              >
                v{{ asset.latestNode.version }}
              </v-chip>
            </div>

            <!-- Hover Menu -->
            <div class="asset-actions" @click.stop>
              <v-menu top offset-y right>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    x-small
                    fab
                    v-bind="attrs"
                    v-on="on"
                    @click.stop
                  >
                    <v-icon small>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item v-if="!readonly && canEditMarkup" @click="uploadNewVersion(asset)">
                    <v-list-item-icon class="mr-2">
                      <v-icon>mdi-content-duplicate</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ $t('markup.new_version') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="approveAsset(asset)">
                    <v-list-item-icon class="mr-2">
                      <v-icon>mdi-list-status</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ $t('markup.approval') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="downloadAsset(asset)">
                    <v-list-item-icon class="mr-2">
                      <v-icon>mdi-download-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ $t('download') }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="!readonly && canEditMarkup" @click="requestRemoval(asset)">
                    <v-list-item-icon class="mr-2">
                      <v-icon>mdi-delete-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ $t('markup.remove_asset') }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </v-card>

          <!-- Asset Title Below Card -->
          <div class="asset-title mt-2 px-1">
            <div class="text-caption text-truncate" :title="asset.name">
              {{ asset.name || $t('markup.unnamed_asset') }}
            </div>
          </div>
        </div>

        <!-- Add New Asset Card -->
        <div class="asset-card" v-if="!readonly && canEditMarkup">
          <v-tooltip top :disabled="!isNewFormat">
            <template v-slot:activator="{ on, attrs }">
              <v-card
                outlined
                class="asset-card-inner asset-card-add"
                :class="{ 'asset-card-disabled': isNewFormat }"
                @click="isNewFormat ? null : uploadNewAsset()"
                v-bind="attrs"
                v-on="on"
              >
                <div class="asset-thumbnail">
                  <div class="asset-thumbnail-placeholder">
                    <v-icon size="48" :color="isNewFormat ? 'grey' : 'primary'">mdi-plus</v-icon>
                  </div>
                </div>
              </v-card>
            </template>
            <span>{{ $t('markup.save_format_first') }}</span>
          </v-tooltip>

          <!-- Add Title Below Card -->
          <div class="asset-title mt-2 px-1">
            <div class="text-caption" :class="isNewFormat ? 'grey--text' : 'primary--text'">
              {{ $t('markup.new_file') }}
            </div>
          </div>
        </div>
      </div>
      </div>
    </template>

    <!-- Remove Asset Confirmation Dialog -->
    <DefaultModal
      ref="removeAssetModal"
      :title="$t('markup.remove_asset')"
      deleteModal
      submit-button-text="delete"
      @submit="confirmRemoval"
      @modal-closed="clearAssetToRemove"
    >
      <template v-slot:content>
        {{ $t('markup.remove_asset_confirmation') }}
      </template>
    </DefaultModal>

    <!-- Markup Session Dialog -->
    <v-dialog v-model="markupSessionDialog" max-width="90vw" max-height="90vh" @input="onMarkupDialogChange">
      <v-card>
        <v-card-title class="d-flex align-center">
          <span>{{ selectedAssetForReview?.name || $t('markup.asset_review') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeMarkupSessionDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0" style="height: 80vh;">
          <!-- Markup session view embedded here -->
          <MarkupSession
            v-if="selectedAssetForReview?.markup_session_id"
            :sessionId="selectedAssetForReview.markup_session_id"
            :hideToolbar="true"
            :canEditMarkup="!readonly && canEditMarkup"
            :useJobApprovalVideoPlayer="true"
            @status-updated="handleStatusUpdate"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Upload Dialog -->
    <v-dialog v-model="uploadDialog" max-width="600px">
      <v-card>
        <v-card-title>
          {{ uploadMode === 'new' ? $t('markup.upload_new_asset') : $t('markup.upload_new_version') }}
        </v-card-title>
        <v-card-text>
          <v-form ref="uploadForm" v-model="formValid">
            <!-- Selection Mode Tabs -->
            <v-tabs v-model="selectionMode" class="mb-4">
              <v-tab>
                <v-icon left>mdi-upload</v-icon>
                {{ $t('markup.upload_file') }}
              </v-tab>
              <v-tab>
                <v-icon left>mdi-folder-search</v-icon>
                {{ $t('markup.select_from_contents') }}
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="selectionMode">
              <!-- Upload File Tab -->
              <v-tab-item>
                <v-file-input
                  v-model="fileToUpload"
                  :label="$t('markup.select_file')"
                  :rules="[validateFileSelected, validateFilename]"
                  :multiple="uploadMode === 'new'"
                  outlined
                  dense
                  show-size
                  prepend-icon="mdi-paperclip"
                  @change="onFileSelected"
                ></v-file-input>

                <v-text-field
                  v-if="uploadMode === 'new' && !isBatchNewAssetUpload"
                  v-model="newAssetName"
                  :label="$t('markup.asset_name')"
                  outlined
                  dense
                  :rules="[
                    v => !!v || $t('field_required'),
                    validateAssetName
                  ]"
                ></v-text-field>
              </v-tab-item>

              <!-- Select from Contents Tab -->
              <v-tab-item>
                <SelectFileField
                  ref="selectFileField"
                  :label="$t('markup.select_file_from_contents')"
                  :parent-node-id="contentRootNodeId"
                  :value="selectedContentNodeId"
                  @selectNodes="onContentFileSelected"
                  class="mt-4"
                />

                <v-text-field
                  v-if="uploadMode === 'new'"
                  v-model="newAssetName"
                  :label="$t('markup.asset_name')"
                  outlined
                  dense
                  class="mt-4"
                  :rules="[
                    v => !!v || $t('field_required'),
                    validateAssetName
                  ]"
                ></v-text-field>
              </v-tab-item>
            </v-tabs-items>
          </v-form>

          <v-alert v-if="uploadError" type="error" dense text class="mt-2">
            {{ uploadError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeUploadDialog">{{ $t('cancel') }}</v-btn>
          <v-btn
            color="primary"
            @click="confirmUpload"
            :loading="uploading"
            :disabled="!canConfirmUpload"
          >
            <v-icon left>{{ selectionMode === 0 ? 'mdi-upload' : 'mdi-check' }}</v-icon>
            {{ selectionMode === 0 ? $t('markup.upload') : $t('markup.select') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import JobDeliverAssetAPI from '@/services/api/job/job_deliver_asset'
import DefaultModal from '@/components/DefaultModal.vue'
import SelectFileField from '@/components/content/default/Form/SelectFileField.vue'
import MarkupSession from '@/views/content/markup/MarkupSession.vue'
import AppActions from '@/store/app/actions-types'
import ContentActions from '@/store/content/actions/actions-types'
import { BgActionEventBus } from '@/bgActionEventBus'

export default {
  name: 'FormatAssetManagement',

  components: {
    DefaultModal,
    SelectFileField,
    MarkupSession
  },

  props: {
    formatInstance: {
      type: Object,
      required: true
    },
    jobId: {
      type: [String, Number],
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    },
    canEditMarkup: {
      type: Boolean,
      default: true
    },
    contentRootNodeId: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      collapsed: false,
      loading: false,
      assetsLoaded: false,
      assetData: null, // Store the loaded asset data
      uploadDialog: false,
      markupSessionDialog: false,
      assetToRemove: null,
      selectedAssetForReview: null,
      uploadMode: 'new', // 'new' or 'version'
      selectionMode: 0, // 0 = upload, 1 = select from contents
      fileToUpload: null,
      selectedContentFile: null,
      selectedContentNodeId: null,
      newAssetName: '',
      selectedSession: null,
      uploading: false,
      uploadError: null,
      formValid: false,
      selectedAssetNodeIds: [],
      pendingDownloadActions: new Set(),
      bgActionHandler: null,
      // The room this format's assets live under (a format's own
      // folder_node_id, separate from other formats' rooms and from the
      // "current" navigation room). Each FormatAssetManagement instance
      // subscribes to and listens on its own room directly against the
      // shared websocket -- the same way EditSyntheticMedia.vue does --
      // instead of going through App.vue's single-room bus, since several
      // instances of this component can be mounted at once (one per format).
      thumbnailBgActionRoom: null,
      thumbnailSocketHandler: null
    }
  },

  created() {
    this.bgActionHandler = this.handleDownloadBgAction
    BgActionEventBus.$on('bgAction', this.bgActionHandler)
  },

  mounted() {
    // Load assets when component is mounted (format is expanded)
    this.fetchAssets()
    this.registerThumbnailBgActionRoom()
  },

  beforeDestroy() {
    if (this.bgActionHandler) {
      BgActionEventBus.$off('bgAction', this.bgActionHandler)
    }

    this.releaseThumbnailBgActionRoom()
  },

  watch: {
    // Re-load assets if formatInstance changes
    'formatInstance.format_instance_id'() {
      this.assetsLoaded = false
      this.selectedAssetNodeIds = []
      this.fetchAssets()
    },

    'formatInstance.job_deliver.folder_node_id'() {
      this.registerThumbnailBgActionRoom()
    },

    'formatInstance.folder_node_id'() {
      this.registerThumbnailBgActionRoom()
    },

    // The socket connects asynchronously after the component mounts (or can
    // be torn down/recreated on reconnect), so the room must be (re)joined
    // and the listener re-attached whenever it becomes available.
    socket(newSocket) {
      if (!newSocket) {
        this.thumbnailSocketHandler = null
        return
      }

      this.attachThumbnailSocketListener()
    }
  },

  computed: {
    ...mapState('app', {
      socket: state => state.socket
    }),

    jobDeliverAssets() {
      // Use the lazily-loaded asset data instead of the prop data
      if (!this.assetData) return []

      // The API returns an array of job_deliver_asset objects
      if (Array.isArray(this.assetData)) {
        return this.assetData
      }

      // If it's an object with job_deliver_asset property (old format)
      if (this.assetData.job_deliver_asset) {
        const assetDataFromLoad = this.assetData.job_deliver_asset
        if (Array.isArray(assetDataFromLoad)) {
          return assetDataFromLoad
        }
        return [assetDataFromLoad]
      }

      return []
    },

    assets() {
      // Transform each job_deliver_asset into an asset card
      return this.jobDeliverAssets.map(assetData => {
        const markupSession = assetData.markup_session

        if (!markupSession) {
          return null
        }

        const markupSessionNodes = markupSession.markup_session_node || []

        // Find the latest version (highest version number)
        const latestNode = this.getLatestVersionNode(markupSessionNodes)

        // The API omits nodes the current user cannot access.
        // Hide the entire card when no accessible version remains.
        if (!latestNode?.node_id) {
          return null
        }

        // Extract markup_session_id - could be markup_session_id or id
        const sessionId = markupSession.markup_session_id || markupSession.id

        return {
          job_deliver_asset_id: assetData.job_deliver_asset_id,
          markup_session_id: sessionId,
          name: markupSession.name || this.$t('markup.unnamed_asset'),
          status: latestNode ? latestNode.status : 'no_status',
          thumbnail: latestNode ? this.getThumbnailFromNode(latestNode) : null,
          latestNode: latestNode,
          allNodes: markupSessionNodes,
          currentVersion: latestNode ? latestNode.version : 1
        }
      }).filter(asset => asset !== null)
    },

    formatVersion() {
      // Get the highest version across all assets
      let maxVersion = 0

      this.assets.forEach(asset => {
        if (asset.currentVersion > maxVersion) {
          maxVersion = asset.currentVersion
        }
      })

      return maxVersion > 0 ? String(maxVersion).padStart(2, '0') : '01'
    },

    canConfirmUpload() {
      // For upload mode (tab 0)
      if (this.selectionMode === 0) {
        if (!this.filesToUpload.length) return false
        if (this.uploadMode === 'new' && !this.isBatchNewAssetUpload && !this.newAssetName) return false
        // Check form validation - always required for file upload
        if (!this.formValid) return false
        return true
      }

      // For content selection mode (tab 1)
      // SelectFileField is outside the form, so we don't check formValid here
      if (this.selectionMode === 1) {
        if (!this.selectedContentFile) return false
        // For new mode, check if asset name is provided and passes validation
        if (this.uploadMode === 'new') {
          if (!this.newAssetName) return false
          // Manually validate the asset name since SelectFileField is outside the form
          const nameValidation = this.validateAssetName(this.newAssetName)
          if (nameValidation !== true) return false
        }
        // For version mode, no additional validation needed beyond having a file
        return true
      }

      return false
    },

    filesToUpload() {
      if (!this.fileToUpload) return []
      return Array.isArray(this.fileToUpload) ? this.fileToUpload : [this.fileToUpload]
    },

    isBatchNewAssetUpload() {
      return this.uploadMode === 'new' && this.filesToUpload.length > 1
    },

    isNewFormat() {
      // Check if the format is new (not saved yet) by looking for format_instance_id
      // New formats can have null, undefined, or -1 as format_instance_id
      const formatId = this.formatInstance?.format_instance_id
      return !formatId || formatId === -1
    }
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_ERROR_MESSAGE,
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
      AppActions.SUBSCRIBE_TO_ROOM,
      AppActions.UNSUBSCRIBE_FROM_ROOM
    ]),
    ...mapActions('content/actions', [
      ContentActions.UPLOAD_FILE,
      ContentActions.DOWNLOAD_FILE,
      ContentActions.SHARE_MULTI_NODE_DOWNLOAD
    ]),

    getLatestVersionNode(markupSessionNodes) {
      // Find the node with the highest version number
      if (!markupSessionNodes || markupSessionNodes.length === 0) {
        return null
      }

      return markupSessionNodes.reduce((latest, current) => {
        if (!latest || current.version > latest.version) {
          return current
        }
        return latest
      }, null)
    },

    getThumbnailFromNode(node) {
      // Get the last rendition of type "thumb", or fallback to "preview"
      if (!node || !node.renditions || node.renditions.length === 0) {
        return null
      }

      // Filter renditions by type
      const thumbRenditions = node.renditions.filter(r => r.type === 'thumb')
      const previewRenditions = node.renditions.filter(r => r.type === 'preview')

      // Use thumb renditions if available, otherwise use preview
      const renditionsToUse = thumbRenditions.length > 0 ? thumbRenditions : previewRenditions

      if (renditionsToUse.length === 0) {
        return null
      }

      // Find the rendition with the highest order
      const lastRendition = renditionsToUse.reduce((last, current) => {
        const currentOrder = parseInt(current.order, 10)
        const lastOrder = parseInt(last.order, 10)

        if (currentOrder > lastOrder) {
          return current
        }
        return last
      }, renditionsToUse[0])

      return lastRendition.url
    },

    uploadNewAsset() {
      if (this.readonly || !this.canEditMarkup) return

      this.uploadMode = 'new'
      this.fileToUpload = null
      this.newAssetName = ''
      this.uploadError = null
      this.selectedSession = null
      this.uploadDialog = true
    },

    uploadNewVersion(asset) {
      if (this.readonly || !this.canEditMarkup) return

      this.uploadMode = 'version'
      this.fileToUpload = null
      this.uploadError = null
      this.selectedSession = asset
      this.uploadDialog = true
    },

    onFileSelected(file) {
      const files = Array.isArray(file) ? file : (file ? [file] : [])

      if (files.length === 1 && this.uploadMode === 'new' && !this.newAssetName) {
        // Auto-fill name from filename
        this.newAssetName = files[0].name.replace(/\.[^/.]+$/, '')
      } else if (files.length > 1) {
        this.newAssetName = ''
      }
    },

    onContentFileSelected(nodes) {
      if (nodes && nodes.length > 0) {
        this.selectedContentFile = nodes[0]
        this.selectedContentNodeId = this.selectedContentFile.node_id

        // Auto-fill name from filename if creating new asset
        if (this.uploadMode === 'new' && !this.newAssetName) {
          this.newAssetName = this.selectedContentFile.name?.replace(/\.[^/.]+$/, '') || ''
        }
      } else {
        this.selectedContentFile = null
        this.selectedContentNodeId = null
      }
    },

    closeUploadDialog() {
      this.uploadDialog = false
      this.selectionMode = 0
      this.fileToUpload = null
      this.selectedContentFile = null
      this.selectedContentNodeId = null
      this.newAssetName = ''
      this.uploadError = null
    },

    async confirmUpload() {
      if (this.readonly || !this.canEditMarkup) return

      try {
        this.uploading = true
        this.uploadError = null

        // Check which mode we're in
        if (this.selectionMode === 0) {
          // Upload mode
          if (!this.filesToUpload.length) return

          if (this.uploadMode === 'new') {
            await this.createNewAssets()
          } else {
            await this.addVersionToAsset()
          }
        } else {
          // Content selection mode
          if (!this.selectedContentFile) return

          if (this.uploadMode === 'new') {
            await this.createNewAssetFromContent()
          } else {
            await this.addVersionFromContent()
          }
        }

        this.closeUploadDialog()

        // Reset loaded flag and reload assets
        this.assetsLoaded = false
        await this.fetchAssets()

        // Emit event to parent to reload deliverables
        this.$emit('asset-uploaded')

        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('markup.asset_uploaded_successfully'))
      } catch (error) {
        console.error('Error uploading asset:', error)
        this.uploadError = error.response?.data?.message || this.$t('markup.error_uploading_asset')
      } finally {
        this.uploading = false
      }
    },

    async createNewAssets() {
      const files = this.filesToUpload
      const concurrencyCount = parseInt(process.env.VUE_APP_UPLOAD_CONCURRENCY) || 3

      // Preparing an asset may create or traverse shared folders. Keep this
      // phase sequential so simultaneous requests do not contend for the
      // read/write locks of the same folder tree.
      const preparedAssets = []
      for (const file of files) {
        const assetName = this.isBatchNewAssetUpload
          ? file.name.replace(/\.[^/.]+$/, '')
          : this.newAssetName

        preparedAssets.push(await this.prepareNewAsset(file, assetName))
      }

      const batches = Array.from(
        { length: Math.min(concurrencyCount, preparedAssets.length) },
        () => []
      )

      preparedAssets.forEach((asset, index) => {
        batches[index % batches.length].push(asset)
      })

      await Promise.all(
        batches.map(async (batch) => {
          for (const asset of batch) {
            await this.uploadPreparedAsset(asset)
          }
        })
      )
    },

    async prepareNewAsset(file, assetName) {
      const prepareResponse = await JobDeliverAssetAPI.prepareStructure({
        job_id: this.jobId,
        format_instance_id: this.formatInstance.format_instance_id,
        markup_session_name: assetName,
        markup_session_id: null
      })

      return {
        file,
        nodeId: prepareResponse.data.node_id,
        markupSessionId: prepareResponse.data.markup_session_id,
      }
    },

    async uploadPreparedAsset({ file, nodeId, markupSessionId }) {
      const uploadResult = await this[ContentActions.UPLOAD_FILE]({
        parentId: nodeId,
        file,
        fieldValues: {
          no_auto_link_job: true
        }
      })
      const uploadedNodeId = uploadResult.nodeId

      await JobDeliverAssetAPI.linkAsset({
        job_id: this.jobId,
        format_instance_id: this.formatInstance.format_instance_id,
        node_id: uploadedNodeId,
        markup_session_id: markupSessionId,
        version: 1
      })
    },

    async addVersionToAsset() {
      if (!this.selectedSession) return

      // Calculate next version - highest version + 1
      const allNodes = this.selectedSession.allNodes || []
      const maxVersion = allNodes.reduce((max, node) => {
        return node.version > max ? node.version : max
      }, 0)
      const nextVersion = maxVersion + 1

      // 1. Call prepareStructure to get the node_id where file should be uploaded
      // Pass the existing markup_session_id so it knows this is a new version
      const prepareResponse = await JobDeliverAssetAPI.prepareStructure({
        job_id: this.jobId,
        format_instance_id: this.formatInstance.format_instance_id,
        markup_session_id: this.selectedSession.markup_session_id
      })

      const { node_id } = prepareResponse.data

      // 2. Upload file to the node_id returned by prepareStructure using content action
      const uploadResult = await this[ContentActions.UPLOAD_FILE]({
        parentId: node_id,
        file: this.fileToUpload,
        fieldValues: {
          no_auto_link_job: true
        }
      })
      const uploadedNodeId = uploadResult.nodeId

      // 3. Link the uploaded file as a new version to the existing markup session
      await JobDeliverAssetAPI.linkAsset({
        job_id: this.jobId,
        format_instance_id: this.formatInstance.format_instance_id,
        node_id: uploadedNodeId,
        markup_session_id: this.selectedSession.markup_session_id,
        version: nextVersion
      })
    },

    async createNewAssetFromContent() {
      // Create the markup session first so it uses the dialog name (or the
      // filename auto-filled when the user leaves the field unchanged).
      // Linking with a null session lets the backend derive a name from the
      // existing node instead, ignoring newAssetName.
      const prepareResponse = await JobDeliverAssetAPI.prepareStructure({
        format_instance_id: this.formatInstance.format_instance_id,
        markup_session_name: this.newAssetName,
        markup_session_id: null
      })

      await JobDeliverAssetAPI.linkAsset({
        job_id: this.jobId,
        format_instance_id: this.formatInstance.format_instance_id,
        node_id: this.selectedContentFile.node_id,
        markup_session_id: prepareResponse.data.markup_session_id,
        version: 1
      })
    },

    async addVersionFromContent() {
      if (!this.selectedSession) return

      // Calculate next version
      const allNodes = this.selectedSession.allNodes || []
      const maxVersion = allNodes.reduce((max, node) => {
        return node.version > max ? node.version : max
      }, 0)
      const nextVersion = maxVersion + 1

      // Link the existing content node as a new version
      await JobDeliverAssetAPI.linkAsset({
        job_id: this.jobId,
        format_instance_id: this.formatInstance.format_instance_id,
        node_id: this.selectedContentFile.node_id,
        markup_session_id: this.selectedSession.markup_session_id,
        version: nextVersion
      })
    },

    openMarkupSession(asset) {
      // Navigate to markup session view if available
      if (asset.markup_session_id) {
        this.$router.push({
          name: 'MarkupSession',
          params: {
            markupSessionId: asset.markup_session_id
          }
        })
      }
    },

    approveAsset(asset) {
      // Open markup session dialog for asset review
      this.selectedAssetForReview = asset
      this.markupSessionDialog = true
    },

    requestRemoval(asset) {
      if (this.readonly || !this.canEditMarkup) return

      this.assetToRemove = asset
      this.$refs.removeAssetModal.openModal()
    },

    clearAssetToRemove() {
      this.assetToRemove = null
    },

    async confirmRemoval() {
      if (this.readonly || !this.canEditMarkup) return

      if (!this.assetToRemove) {
        this.$refs.removeAssetModal.setIsSubmitting(false)
        return
      }

      try {
        const asset = this.assetToRemove
        await JobDeliverAssetAPI.delete(asset.job_deliver_asset_id, this.jobId)

        this.assetsLoaded = false
        await this.fetchAssets()
        this.$emit('asset-uploaded')
        this.$refs.removeAssetModal.closeModal()
        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('markup.asset_removed_successfully'))
      } catch (error) {
        console.error('Error removing approval/markup process:', error)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](
          error.response?.data?.message || this.$t('markup.error_removing_asset')
        )
        this.$refs.removeAssetModal.setIsSubmitting(false)
      }
    },

    closeMarkupSessionDialog() {
      this.markupSessionDialog = false
      this.selectedAssetForReview = null
    },

    onMarkupDialogChange(isOpen) {
      // Clear selected asset data when dialog is closed (by any means)
      if (!isOpen) {
        this.selectedAssetForReview = null
      }
    },

    handleStatusUpdate(statusData) {
      // Update the local asset data without refetching
      // statusData should contain: { nodeId, status, version }
      if (!this.assetData || !Array.isArray(this.assetData)) return

      // Find the asset and update its status
      this.assetData.forEach(assetItem => {
        if (assetItem.markup_session?.markup_session_node) {
          const nodes = assetItem.markup_session.markup_session_node
          const nodeToUpdate = nodes.find(n =>
            n.node_id === statusData.nodeId ||
            (n.version === statusData.version && n.markup_session_id === statusData.sessionId)
          )

          if (nodeToUpdate) {
            // Update the status
            this.$set(nodeToUpdate, 'status', statusData.status)
            this.$set(nodeToUpdate, 'updated_at', new Date().toISOString())
          }
        }
      })

      // Force reactivity update
      this.assetData = [...this.assetData]
    },

    validateFileSelected(file) {
      const files = Array.isArray(file) ? file : (file ? [file] : [])
      if (!files.length) {
        return String(this.$t('form_rules.mandatory'))
      }
      return true
    },

    validateFilename(file) {
      const files = Array.isArray(file) ? file : (file ? [file] : [])

      for (const selectedFile of files) {
        const name = selectedFile.name

        // eslint-disable-next-line no-control-regex
        const invalidChars = /[\\/:*?"<>|\x00-\x1F\x7F]/
        const reservedNames =
          ['CON', 'PRN', 'AUX', 'NUL',
          'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
          'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9']

        const baseName = name.replace(/\.[^/.]+$/, '')

        if (
          invalidChars.test(name) ||
          name.length > 255 ||
          reservedNames.includes(baseName.toUpperCase()) ||
          /[. ]$/.test(name)
        ) {
          return String(this.$t('form_rules.invalid_filename'))
        }
      }

      return true
    },

    validateAssetName(name) {
      if (!name) return true

      // eslint-disable-next-line no-control-regex
      const invalidChars = /[\\/:*?"<>|\x00-\x1F\x7F]/
      const reservedNames =
        ['CON', 'PRN', 'AUX', 'NUL',
        'COM1', 'COM2', 'COM3', 'COM4', 'COM5', 'COM6', 'COM7', 'COM8', 'COM9',
        'LPT1', 'LPT2', 'LPT3', 'LPT4', 'LPT5', 'LPT6', 'LPT7', 'LPT8', 'LPT9']

      if (
        invalidChars.test(name) ||
        name.length > 255 ||
        reservedNames.includes(name.toUpperCase()) ||
        /[. ]$/.test(name)
      ) {
        return String(this.$t('form_rules.invalid_filename'))
      }

      return true
    },

    isAssetSelected(asset) {
      return !!asset.latestNode?.node_id && this.selectedAssetNodeIds.includes(asset.latestNode.node_id)
    },

    toggleAssetSelection(asset, selected) {
      const nodeId = asset.latestNode?.node_id
      if (!nodeId) return

      if (selected) {
        if (!this.selectedAssetNodeIds.includes(nodeId)) {
          this.selectedAssetNodeIds.push(nodeId)
        }
        return
      }

      this.selectedAssetNodeIds = this.selectedAssetNodeIds.filter(id => id !== nodeId)
    },

    async downloadAsset(asset) {
      // Download the latest node using content action
      if (!asset.latestNode || !asset.latestNode.node_id) {
        console.error('No node available to download')
        return
      }

      // Create a control object to track downloading state
      const control = { downloading: false }

      this[ContentActions.DOWNLOAD_FILE]({
        nodeId: asset.latestNode.node_id,
        control: control
      })
    },

    openContentsFolder() {
      const folderNodeId = this.formatInstance?.job_deliver?.folder_node_id
      if (!folderNodeId) return

      const contentAppId = this.$route.params.contentAppId

      const { href } = this.$router.resolve({
        name: 'ContentNavigation',
        params: { appId: contentAppId, parentId: folderNodeId }
      })

      window.open(href, '_blank', 'noopener')
    },

    async downloadSelectedAssets() {
      if (!this.contentRootNodeId) {
        console.error('No content root node available for multi-node download')
        return
      }

      const bgActionId = `bg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
      // The Deliverables route does not expose a content `parentId`; tell
      // App.vue which folder room must receive the asynchronous ZIP update.
      BgActionEventBus.$emit('register-room', this.contentRootNodeId)
      // Track the id before the HTTP request in case ZIP creation finishes
      // before its response is returned.
      this.pendingDownloadActions.add(bgActionId)

      try {
        await this[ContentActions.SHARE_MULTI_NODE_DOWNLOAD]({
          parentNodeId: this.contentRootNodeId,
          nodeIds: this.selectedAssetNodeIds,
          bgActionId
        })
      } catch (error) {
        this.pendingDownloadActions.delete(bgActionId)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](error.response?.data?.message || error.message)
      }
    },

    handleDownloadBgAction(message) {
      const bgActionId = message.bgActionId ?? message.bg_action_id
      if (!this.pendingDownloadActions.has(bgActionId)) {
        return
      }

      const status = String(message.status || '').toLowerCase()
      if (status === 'error') {
        this.pendingDownloadActions.delete(bgActionId)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](message.error || this.$t('error'))
        return
      }

      if (!['done', 'success'].includes(status)) {
        return
      }

      const downloadUrl = this.getZipDownloadUrl(message)
      if (!downloadUrl) {
        return
      }

      // Avoid duplicate browser downloads if the socket replays the event.
      this.pendingDownloadActions.delete(bgActionId)
      const link = document.createElement('a')
      link.target = '_blank'
      link.href = downloadUrl
      link.click()
    },

    getZipDownloadUrl(message) {
      let payload = message.text
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload)
        } catch (error) {
          return null
        }
      }

      const downloadUrl = payload?.download_url
      if (typeof downloadUrl !== 'string') {
        return null
      }

      // The API may provide the URL as a Markdown link.
      const markdownUrl = downloadUrl.match(/^\[[\s\S]*\]\((https?:\/\/[^)]+)\)$/)
      try {
        return new URL(markdownUrl ? markdownUrl[1] : downloadUrl).toString()
      } catch (error) {
        console.error('[share-download] invalid ZIP download URL', { message, error })
        return null
      }
    },

    // Each format instance has its own asset folder (formatInstance's own
    // folder_node_id, falling back to job_deliver's), separate from the
    // other formats rendered alongside it and from the "current" navigation
    // room. Several instances of this component can be mounted at once (one
    // per format), so this subscribes directly against the shared websocket
    // -- via SUBSCRIBE_TO_ROOM/UNSUBSCRIBE_FROM_ROOM and socket.on/off on the
    // room name, the same pattern EditSyntheticMedia.vue uses -- instead of
    // going through App.vue's single "current room" bus, which can only
    // track one room at a time.
    registerThumbnailBgActionRoom() {
      const folderNodeId = this.formatInstance?.job_deliver?.folder_node_id
        ?? this.formatInstance?.folder_node_id

      if (!folderNodeId || String(folderNodeId) === String(this.thumbnailBgActionRoom)) {
        return
      }

      this.releaseThumbnailBgActionRoom()
      this.thumbnailBgActionRoom = folderNodeId
      this.attachThumbnailSocketListener()
    },

    releaseThumbnailBgActionRoom() {
      if (this.thumbnailBgActionRoom === null || this.thumbnailBgActionRoom === undefined) {
        return
      }

      const roomName = String(this.thumbnailBgActionRoom)

      if (this.socket && this.thumbnailSocketHandler) {
        this.socket.off(roomName, this.thumbnailSocketHandler)
      }

      this[AppActions.UNSUBSCRIBE_FROM_ROOM](roomName)
      this.thumbnailSocketHandler = null
      this.thumbnailBgActionRoom = null
    },

    attachThumbnailSocketListener() {
      if (!this.socket || this.thumbnailBgActionRoom === null || this.thumbnailBgActionRoom === undefined) {
        return
      }

      if (this.thumbnailSocketHandler) {
        return
      }

      const roomName = String(this.thumbnailBgActionRoom)
      this.thumbnailSocketHandler = (message) => this.handleThumbnailBgAction(message, roomName)
      this.socket.on(roomName, this.thumbnailSocketHandler)
      this[AppActions.SUBSCRIBE_TO_ROOM](roomName)
    },

    // Mirrors Navigation.vue's handleBgAction/handleCompletedBgActionMessage:
    // once a bgAction completes for a node_id that matches one of the asset
    // versions currently rendered, silently reload the assets so the
    // thumbnail (or status) reflects the freshly generated rendition.
    handleThumbnailBgAction(message) {
      if (!this.isCompletedBgActionMessage(message)) {
        return
      }

      const nodeId = this.getBgActionNodeId(message)

      if (!nodeId || !this.isAssetNode(nodeId)) {
        return
      }

      this.assetsLoaded = false
      this.fetchAssets()
    },

    isCompletedBgActionMessage(message) {
      return ['success', 'done', 'error'].includes(String(message.status).toLowerCase())
    },

    getBgActionNodeId(message) {
      return message.node_id ?? message.nodeId
    },

    isAssetNode(nodeId) {
      return this.assets.some(asset =>
        (asset.allNodes || []).some(node => String(node.node_id) === String(nodeId))
      )
    },

    async fetchAssets() {
      // Check if we have the necessary data
      const jobDeliverId = this.formatInstance?.job_deliver?.job_deliver_id
      const jobId = this.jobId

      if (!jobDeliverId || !jobId) {
        console.warn('No job_deliver_id or jobId found, cannot load assets')
        this.assetData = null
        this.assetsLoaded = true
        return
      }

      // If already loaded, skip
      if (this.assetsLoaded) {
        return
      }

      try {
        this.loading = true

        // Call the new endpoint to get assets data
        const response = await JobDeliverAssetAPI.readAssets(jobDeliverId, jobId)

        // Store the response data
        this.assetData = response.data || null
        const availableNodeIds = new Set(
          this.assets.map(asset => asset.latestNode?.node_id).filter(Boolean)
        )
        this.selectedAssetNodeIds = this.selectedAssetNodeIds.filter(nodeId => availableNodeIds.has(nodeId))
        this.assetsLoaded = true
      } catch (error) {
        console.error('Error loading assets:', error)
        this.assetData = null
        this.assetsLoaded = true
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.format-asset-management {
  margin-top: 16px;
}

.assets-container {
  margin-top: 12px;
}

.assets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.asset-card {
  display: flex;
  flex-direction: column;

  &-inner {
    cursor: pointer;
    transition: all 0.2s;
    position: relative;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;

      .asset-actions {
        opacity: 1;
      }
    }
  }

  &-add {
    border: 2px dashed #ccc !important;

    &:hover {
      border-color: var(--v-primary-base) !important;
      background-color: rgba(var(--v-primary-base), 0.02);
    }
  }

  &-disabled {
    cursor: not-allowed !important;
    opacity: 0.5;

    &:hover {
      transform: none !important;
      box-shadow: none !important;
      border-color: #ccc !important;
      background-color: transparent !important;
    }
  }
}

.asset-thumbnail {
  position: relative;
  width: 100%;
  padding-bottom: 100%; /* 1:1 aspect ratio - creates a square */
  overflow: hidden;
  background-color: #f5f5f5;
  border-radius: 4px; /* Match v-card default border radius */

  .v-image,
  &-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.asset-status-badge {
  position: absolute;
  right: 6px;
  bottom: 6px;
}

.asset-latest-version-badge {
  position: absolute;
  top: 6px;
  right: 6px;
  font-weight: 600;
}

.asset-selection-checkbox {
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: 1;
}

.asset-actions {
  position: absolute;
  bottom: 6px;
  left: 6px;
  opacity: 0;
  transition: opacity 0.2s;
}

.asset-title {
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.2;
}
</style>
